/**
 * Compute customer cancel-window status from an order document client-side.
 * Mirrors the backend `_compute_cancel_window` so we can show countdowns
 * without an extra API roundtrip.
 *
 * Pre-acceptance: customer can cancel anytime.
 * Post-acceptance: customer has `windowHours` (default 3) from `accepted_at`.
 * Delivered / cancelled orders: always blocked.
 */
const DEFAULT_WINDOW_HOURS = 3;

export function cancelWindowFor(order, windowHours = DEFAULT_WINDOW_HOURS) {
  const status = (order && order.status) || "pending";
  if (status === "cancelled") {
    return { accepted: !!order?.accepted_at, can_cancel: false, reason: "Already cancelled", deadline: null, ms_remaining: null };
  }
  if (status === "delivered" || status === "return_delivered") {
    return { accepted: true, can_cancel: false, reason: "Already delivered", deadline: null, ms_remaining: null };
  }
  if (status === "pending" || !order?.accepted_at) {
    return { accepted: false, can_cancel: true, reason: null, deadline: null, ms_remaining: null };
  }
  const acceptedAt = new Date(order.accepted_at);
  if (Number.isNaN(acceptedAt.getTime())) {
    return { accepted: true, can_cancel: true, reason: null, deadline: null, ms_remaining: null };
  }
  const deadline = new Date(acceptedAt.getTime() + windowHours * 60 * 60 * 1000);
  const msRemaining = deadline.getTime() - Date.now();
  if (msRemaining > 0) {
    return { accepted: true, can_cancel: true, reason: null, deadline, ms_remaining: msRemaining };
  }
  return { accepted: true, can_cancel: false, reason: "Cancellation window expired", deadline, ms_remaining: msRemaining };
}

export function formatRemaining(ms) {
  if (ms === null || ms === undefined) return "";
  if (ms <= 0) return "expired";
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `${h}h ${m}m left`;
  if (m > 0) return `${m}m left`;
  return "<1m left";
}

export const CUSTOMER_CANCEL_WINDOW_HOURS = DEFAULT_WINDOW_HOURS;
